import React, { useEffect, useState } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import "./App.css";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

// UI PREVIEW MODE - Set to false for production
const USE_MOCK_DATA = false;

const generateMockHistoricalData = () => {
  const data = [];
  const startDate = new Date('2025-01-01');
  let nav = 850000;
  let aum = 800000;

  for (let i = 0; i < 365; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);

    // Random daily change between -2% and +2%
    nav = nav * (1 + (Math.random() - 0.48) * 0.02);
    aum = nav * 0.95;

    data.push({
      date: date.toISOString().split('T')[0],
      nav: Math.round(nav * 100) / 100,
      aum: Math.round(aum * 100) / 100
    });
  }
  return data;
};

const HistoricalData = () => {
  const [historicalData, setHistoricalData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [tableFilterStart, setTableFilterStart] = useState("");
  const [tableFilterEnd, setTableFilterEnd] = useState("");
  const [graphFilterStart, setGraphFilterStart] = useState("");
  const [graphFilterEnd, setGraphFilterEnd] = useState("");
  const [graphMetric, setGraphMetric] = useState("nav");
  const API_BASE_URL = "https://fundtracker.reicheltcapital.com/api";

  useEffect(() => {
    fetchHistoricalData();
    const today = new Date().toISOString().split("T")[0];
    const thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split("T")[0];
    setTableFilterStart(thirtyDaysAgo);
    setTableFilterEnd(today);
  }, []);

  const fetchHistoricalData = async () => {
    // Use mock data for UI preview
    if (USE_MOCK_DATA) {
      setTimeout(() => {
        setHistoricalData(generateMockHistoricalData());
        setLoading(false);
      }, 500);
      return;
    }

    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        setError("No authentication token found. Please log in.");
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API_BASE_URL}/get-historical-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(response.data)) {
        setHistoricalData(response.data);
      } else {
        setError("Unexpected data format received from server.");
      }
    } catch (err) {
      setError("Error fetching historical data");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    if (USE_MOCK_DATA) {
      setError("File upload disabled in preview mode");
      return;
    }
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const token = sessionStorage.getItem("authToken");
      await axios.post(`${API_BASE_URL}/upload-historical-data`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      fetchHistoricalData();
    } catch (uploadError) {
      setError("Error uploading file");
    }
  };

  const handleDownloadExcel = () => {
    if (USE_MOCK_DATA) {
      setError("Download disabled in preview mode");
      return;
    }
    window.location.href = `${API_BASE_URL}/download-historical-data`;
  };

  const handleSaveSnapshot = async () => {
    if (USE_MOCK_DATA) {
      setError("Snapshot saved (preview mode)");
      setTimeout(() => setError(""), 2000);
      return;
    }
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        setError("No authentication token found. Please log in.");
        return;
      }
      await axios.get(`${API_BASE_URL}/save-snapshot-now`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setError("Snapshot triggered successfully. Refreshing data...");
      setTimeout(fetchHistoricalData, 2000);
    } catch (err) {
      setError("Error triggering snapshot");
    }
  };

  const filteredTableData = historicalData.filter((entry) => {
    const entryDate = new Date(entry.date);
    return (
      (!tableFilterStart || entryDate >= new Date(tableFilterStart)) &&
      (!tableFilterEnd || entryDate <= new Date(tableFilterEnd))
    );
  });

  const filteredGraphData = historicalData.filter((entry) => {
    const entryDate = new Date(entry.date);
    return (
      (!graphFilterStart || entryDate >= new Date(graphFilterStart)) &&
      (!graphFilterEnd || entryDate <= new Date(graphFilterEnd))
    );
  });

  const calculatePeriodReturn = () => {
    if (!graphFilterStart || !graphFilterEnd || filteredGraphData.length < 2) {
      return null;
    }
    const sortedData = [...filteredGraphData].sort((a, b) => new Date(a.date) - new Date(b.date));
    const startEntry = sortedData[0];
    const endEntry = sortedData[sortedData.length - 1];
    const startingValue = startEntry[graphMetric];
    const endingValue = endEntry[graphMetric];
    if (!startingValue || !endingValue || startingValue === 0) {
      return null;
    }
    const percentReturn = ((endingValue - startingValue) / startingValue) * 100;
    return percentReturn.toFixed(2);
  };

  const periodReturn = calculatePeriodReturn();

  const chartData = {
    labels: filteredGraphData.map((entry) => entry.date),
    datasets: [
      {
        label: graphMetric.toUpperCase(),
        data: filteredGraphData.map((entry) => entry[graphMetric]),
        fill: false,
        backgroundColor: graphMetric === "nav" ? "#4ecdc4" : "#ff6b6b",
        borderColor: graphMetric === "nav" ? "#4ecdc4" : "#ff6b6b",
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: '#e0e0e0' }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: $${context.raw.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { color: '#a0a0a0', maxTicksLimit: 8 },
        grid: { color: 'rgba(255,255,255,0.05)' }
      },
      y: {
        ticks: {
          color: '#a0a0a0',
          callback: (value) => '$' + value.toLocaleString()
        },
        grid: { color: 'rgba(255,255,255,0.05)' }
      },
    },
  };

  const formatCurrency = (num) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);

  if (loading) {
    return (
      <div className="page-container">
        <h1>Historical Data</h1>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <div className="status-badge loading">
            <span className="spinner"></span>
            Loading historical data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1>Historical Data</h1>

      {error && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
          <div className={`status-badge ${error.includes('success') || error.includes('Snapshot') ? 'success' : 'error'}`}>
            {error}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="controls-bar" style={{ marginBottom: '20px' }}>
        <div className="control-group">
          <label>Upload Data</label>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            className="glass-input"
            style={{ padding: '8px' }}
          />
        </div>
        <button className="btn-secondary" onClick={handleDownloadExcel}>
          Download XLSX
        </button>
        <button className="btn-primary" onClick={handleSaveSnapshot}>
          Force Snapshot
        </button>
      </div>

      <div className="stats-grid">
        {/* Table Filters Card */}
        <div className="glass-card">
          <h3>Table Filters</h3>
          <div className="stat-item">
            <span className="stat-label">Start Date</span>
            <input
              type="date"
              className="glass-input"
              value={tableFilterStart}
              onChange={(e) => setTableFilterStart(e.target.value)}
              style={{ width: '150px' }}
            />
          </div>
          <div className="stat-item">
            <span className="stat-label">End Date</span>
            <input
              type="date"
              className="glass-input"
              value={tableFilterEnd}
              onChange={(e) => setTableFilterEnd(e.target.value)}
              style={{ width: '150px' }}
            />
          </div>
          <div className="stat-item" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '16px', marginTop: '8px' }}>
            <span className="stat-label">Records Shown</span>
            <span className="stat-value">{filteredTableData.length}</span>
          </div>
        </div>

        {/* Graph Filters Card */}
        <div className="glass-card">
          <h3>Graph Filters</h3>
          <div className="stat-item">
            <span className="stat-label">Start Date</span>
            <input
              type="date"
              className="glass-input"
              value={graphFilterStart}
              onChange={(e) => setGraphFilterStart(e.target.value)}
              style={{ width: '150px' }}
            />
          </div>
          <div className="stat-item">
            <span className="stat-label">End Date</span>
            <input
              type="date"
              className="glass-input"
              value={graphFilterEnd}
              onChange={(e) => setGraphFilterEnd(e.target.value)}
              style={{ width: '150px' }}
            />
          </div>
          <div className="stat-item">
            <span className="stat-label">Metric</span>
            <select
              className="glass-select"
              value={graphMetric}
              onChange={(e) => setGraphMetric(e.target.value)}
              style={{ width: '150px' }}
            >
              <option value="nav">NAV</option>
              <option value="aum">AUM</option>
            </select>
          </div>
          {periodReturn !== null && (
            <div className="stat-item" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '16px', marginTop: '8px' }}>
              <span className="stat-label">Period Return</span>
              <span className={`stat-value ${parseFloat(periodReturn) >= 0 ? 'positive' : 'negative'}`}>
                {periodReturn}%
              </span>
            </div>
          )}
        </div>

        {/* Chart Card */}
        <div className="glass-card" style={{ gridColumn: 'span 2' }}>
          <h3>{graphMetric.toUpperCase()} Over Time</h3>
          <div style={{ height: '350px' }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* Data Table Card */}
        <div className="glass-card" style={{ gridColumn: 'span 2' }}>
          <h3>Historical Records</h3>
          {filteredTableData.length > 0 ? (
            <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
              <table className="glass-table">
                <thead style={{ position: 'sticky', top: 0 }}>
                  <tr>
                    <th>Date</th>
                    <th style={{ textAlign: 'right' }}>NAV</th>
                    <th style={{ textAlign: 'right' }}>AUM</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTableData.slice().reverse().map((entry, i) => (
                    <tr key={i}>
                      <td>{entry.date}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                        {formatCurrency(entry.nav)}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                        {entry.aum !== null ? formatCurrency(entry.aum) : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
              No historical data available for the selected date range.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoricalData;
